import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { toPortalFailure } from "../lib/errors";
import { BoxState } from "../ui/BoxState";
import { Callout } from "../ui/Callout";
import { AuthFrame } from "./auth/AuthFrame";

/**
 * Where the link mailed to a new sign-in address lands. Added 2026-09-22 in round C with
 * `20260922230001`.
 *
 * **Why the change waits for this page.** Until round C the operations tick changed the address the
 * moment Petrovic asked. A typo then locked the person out and sent their security mail to a
 * stranger, and a hijacked admin session could change the address, remove the client's second factor
 * and reset the password into a taken account. Now nothing changes until this page's button is
 * pressed.
 *
 * **Why it is behind the sign-in.** The link proves somebody reads the new mailbox, and in the chain
 * above the attacker chose that mailbox. What the attacker does not hold is the client's own sign-in.
 * So the route sits behind `RequireSession`, which sends a signed-out visitor to the sign-in screen
 * with this address, token included, as the destination, and sends a session that owes its second
 * factor to `/verify` first. `public.confirm_contact_email_change` refuses any session but the
 * account's own, so the route deciding nothing is safe; it only asks.
 *
 * **Why a button.** The invitation's rule, carried over from `AuthCallbackRoute`: a page load spends
 * nothing, so a mail filter that opens every link in a real browser cannot use this one up. The
 * double-press guard is a ref for the same reason it is one there.
 *
 * Outside the shell, like `/verify`, because it is one question and one button and the shell's
 * navigation would only offer ways to leave before answering it.
 */

type Outcome =
  | { kind: "waiting" }
  | { kind: "working" }
  | { kind: "done"; newEmail: string }
  | { kind: "failed"; message: string; code: string | null };

/**
 * The sentences for the refusals a person can meet here, written to the person holding the link.
 * `app/lib/errors.ts` carries the same codes in words for whoever else reads them; these are the
 * client's.
 */
const REFUSAL: Record<string, string> = {
  PORTAL_EMAIL_CHANGE_LINK_EXPIRED:
    "Länken har gått ut, och ingenting har ändrats. Hör av dig till Petrovic Systems om adressen fortfarande ska bytas, så skickas en ny länk.",
  PORTAL_EMAIL_CHANGE_LINK_USED:
    "Länken är redan använd. Bytet är bekräftat och görs inom någon minut.",
  PORTAL_EMAIL_CHANGE_LINK_INVALID:
    "Länken gick inte att använda, och ingenting har ändrats. Den fungerar bara för kontot som bytet gäller, och slutar fungera om Petrovic Systems avbryter bytet. Är du inloggad på ett annat konto, logga ut och öppna länken igen.",
  PORTAL_EMAIL_CHANGE_FACTOR_REQUIRED:
    "Logga in med koden från din verifieringsapp först. Ingenting har ändrats.",
  PORTAL_CONTACT_INACTIVE:
    "Kontot är inte längre aktivt hos kunden, så adressen byts inte. Ingenting har ändrats.",
  PORTAL_RATE_LIMITED: "För många försök. Vänta en stund och försök igen. Ingenting har ändrats."
};

const TOKEN_SHAPE = /^[0-9a-f]{64}$/;

export function ConfirmEmailChangeRoute() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [outcome, setOutcome] = useState<Outcome>({ kind: "waiting" });
  const pressed = useRef(false);

  async function confirm() {
    if (pressed.current) {
      return;
    }
    pressed.current = true;
    setOutcome({ kind: "working" });

    try {
      const { data, error } = await getSupabaseBrowserClient().rpc("confirm_contact_email_change", {
        p_token: token
      });

      if (error) {
        const failure = toPortalFailure(error);
        setOutcome({
          kind: "failed",
          code: failure.code,
          message: REFUSAL[failure.code] ?? failure.message
        });
        return;
      }

      const answer = (data ?? {}) as { new_email?: unknown };
      setOutcome({
        kind: "done",
        newEmail: typeof answer.new_email === "string" ? answer.new_email : ""
      });
    } catch {
      setOutcome({
        kind: "failed",
        code: null,
        message: "Bekräftelsen gick inte fram. Ingenting har ändrats. Försök igen om en stund."
      });
      pressed.current = false;
    }
  }

  /**
   * Drawn after `confirmAddressPage` in `prototype/kund/pages-auth.js`, approved 2026-09-24, with
   * two differences. The sketch shows the new address in its own row before the press; this page
   * does not know it until the server answers, and asking would be a new read for a page that
   * decides nothing, so the lead keeps saying "the address this link was sent to". And the lead
   * keeps all three sentences: this is the confirmation of a change the client cannot take back on
   * their own, which is the one place the third rule in design.md section 3 leaves a sentence.
   */
  if (outcome.kind === "done") {
    return (
      <AuthFrame titleId="confirm-email-heading">
        <h2 id="confirm-email-heading" className="k-auth-title">
          <CheckCircle size={22} aria-hidden="true" />
          Den nya adressen är bekräftad
        </h2>
        <div className="stack is-tight" data-testid="confirm-email-done">
          <p>
            {outcome.newEmail ? (
              <>
                Inom någon minut loggar du in med <b className="mono">{outcome.newEmail}</b>.
              </>
            ) : (
              "Inom någon minut loggar du in med den nya adressen."
            )}
          </p>
          <p className="k-auth-note">
            Den gamla adressen får ett mejl om bytet, och du får en säkerhetsnotis.
          </p>
        </div>
        <Link className="button is-primary is-block" to="/overview">
          <span>Till portalen</span>
        </Link>
      </AuthFrame>
    );
  }

  if (!TOKEN_SHAPE.test(token) || outcome.kind === "failed") {
    const message =
      outcome.kind === "failed"
        ? outcome.message
        : "Länken är inte hel. Öppna den igen från mejlet, och kopiera hela adressen om du klistrar in den. Ingenting har ändrats.";

    return (
      <AuthFrame titleId="confirm-email-heading">
        <h2 id="confirm-email-heading" className="k-auth-title">
          Adressen kunde inte bekräftas
        </h2>
        <Callout tone="danger" role="alert" testId="confirm-email-failed">
          {message}
        </Callout>
        <Link className="button is-block" to="/overview">
          <span>Till portalen</span>
        </Link>
      </AuthFrame>
    );
  }

  const working = outcome.kind === "working";

  return (
    <AuthFrame titleId="confirm-email-heading">
      <h2 id="confirm-email-heading" className="k-auth-title">
        Bekräfta den nya adressen
      </h2>
      <p>
        Petrovic Systems har bett om att ditt konto ska logga in med adressen som den här länken skickades
        till. Ingenting byts förrän du trycker på knappen. Har du inte bett om ett byte, stäng
        sidan. Då ändras ingenting.
      </p>
      <button
        type="button"
        className="button is-primary is-block"
        data-testid="confirm-email-continue"
        disabled={working}
        aria-busy={working}
        onClick={() => void confirm()}
      >
        <span>Bekräfta den nya adressen</span>
      </button>
      {working ? (
        <BoxState kind="loading" title="Bekräftar" compact />
      ) : (
        <div className="k-auth-links">
          <p className="k-auth-note">
            Knappen finns så att ett e-postprogram som öppnar länkar av sig självt inte kan använda
            upp länken.
          </p>
        </div>
      )}
    </AuthFrame>
  );
}
